import type { StateCreator, StoreApi } from 'zustand';
import { createStore } from 'zustand';
import type { FullState } from './store';

type FunctionalSetField<T> = (state: T) => T;
export type SetField<T> = <Property extends keyof T>(key: Property) => (value: T[Property] | FunctionalSetField<T[Property]>) => void;

function isFunctionalSetField<T>(value: unknown): value is FunctionalSetField<T> {
  return typeof value === 'function';
}

/**
 * Builds a `setField` for a standalone zustand store, mirroring the slice-scoped `setField` below.
 * Use this when a store is created with `create()` rather than as a slice of the root store.
 */
export function createSetField<T extends Record<string, unknown>>(set: StoreApi<T>['setState']): SetField<T> {
  return (key) => (value) => {
    // The casts are safe but unprovable to TS: a computed single-property object built from
    // `keyof T` and `T[key]` is a valid `Partial<T>`, which TS cannot infer.
    if (isFunctionalSetField<T[typeof key]>(value)) {
      set((state) => ({ [key]: value(state[key]) }) as unknown as Partial<T>);
    } else {
      set({ [key]: value } as unknown as Partial<T>);
    }
  };
}

// we want to essentially ensure the root store always has a consistent shape
type RestrictedState = Record<string, unknown>;
export type CentralizedStore<S extends RestrictedState> = StateCreator<S>;

export const createStrictStore = <S extends RestrictedState>(stateCreator: CentralizedStore<S>) => {
  return createStore<S>()(stateCreator);
};

const createSlice = <SliceKey extends keyof FullState>(
  sliceKey: SliceKey,
  { getFull, setFull }: { getFull: StoreApi<FullState>['getState']; setFull: StoreApi<FullState>['setState'] },
) => {
  const get: StoreApi<FullState[SliceKey]>['getState'] = () => getFull()[sliceKey];
  const set: StoreApi<FullState[SliceKey]>['setState'] = (properties, replace = false) => {
    setFull((state: FullState) => {
      const updates = typeof properties === 'function' ? properties(state[sliceKey]) : properties;
      return {
        [sliceKey]: {
          ...(replace ? {} : state[sliceKey]),
          ...updates,
        },
      };
    });
  };

  // a convenience function to set a single field on a slice, either functionally or with a value
  // used in components for simple updates (onChange handlers and such), but not to be overused
  // for anything more than trivial cases, functions should be defined within the store which
  // would then use the standard `set` and `get` functions above
  const setField: SetField<FullState[SliceKey]> = (key) => (value) => {
    if (isFunctionalSetField<FullState[SliceKey][any]>(value)) {
      set((state) => ({ [key]: value(state[key]) }) as any);
    } else {
      set({ [key]: value } as any);
    }
  };

  return { set, get, setField };
};

export const createStoreSlice =
  <K extends keyof FullState, DefaultSliceState extends Partial<FullState[K]>>(
    key: K,
    defaultState: DefaultSliceState,
    create: (createArgs: {
      getFull: StoreApi<FullState>['getState'];
      setFull: StoreApi<FullState>['setState'];
      get: StoreApi<FullState[K]>['getState'];
      set: StoreApi<FullState[K]>['setState'];
      setField: SetField<FullState[K]>;
    }) => Omit<FullState[K], keyof DefaultSliceState>,
  ): ((_: {
    getFull: StoreApi<FullState>['getState'];
    setFull: StoreApi<FullState>['setState'];
  }) => DefaultSliceState & Omit<FullState[K], keyof DefaultSliceState>) =>
  ({ getFull, setFull }) => ({
    ...defaultState,
    ...create({ getFull, setFull, ...createSlice(key, { getFull, setFull }) }),
  });
